import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { OwnSlotPhotos } from "@/components/wo/SlotPhotos";
import { DESTINOS, GPS_FAILS, GPS_RESULTS, ITEM_KINDS, KINDS_BY_BU, PORTON_FIELDS, PORTON_TESTS, UNINSTALL_COND } from "@/lib/techData";

const sel = "h-10 w-full rounded-md border border-border bg-transparent px-2 text-sm";
const Head = ({ children }) => <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">{children}</div>;
const Txt = ({ label, value, onChange, disabled, testid, type }) => (
  <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">{label}</Label>
    <Input className="h-10" type={type} value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>
);
const Sel = ({ label, value, options, onChange, disabled, testid }) => (
  <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">{label}</Label>
    <select className={sel} value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">Seleccionar</option>{options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select></div>
);
const Check = ({ label, checked, onChange, disabled, testid }) => (
  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />{label}</label>
);

const Items = ({ kinds, rows, set, ed, woId }) => (
  <div className="space-y-2" data-testid="td-items">
    <Head>Equipos (datos técnicos)</Head>
    {rows.map((it, i) => (
      <div key={it.id || i} className="space-y-2 rounded-lg border border-border p-3" data-testid={`td-item-${i}`}>
        <div className="flex items-center justify-between text-sm font-medium">{ITEM_KINDS[it.kind][0]}
          {ed && <Button size="icon" variant="ghost" onClick={() => set(rows.filter((_, j) => j !== i))} data-testid={`td-item-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>}</div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {ITEM_KINDS[it.kind][1].map(([f, l]) => <Txt key={f} label={l} value={it[f]} disabled={!ed} onChange={(v) => set(rows.map((x, j) => (j === i ? { ...x, [f]: v } : x)))} testid={`td-item-${i}-${f}`} />)}
        </div>
        {it.kind === "nvr" && woId && (it.id ? <OwnSlotPhotos woId={woId} slot={`vvpat:${it.id}`} label="Patrón de desbloqueo (fotografía, opcional)" editable={ed} testid={`td-item-${i}-pattern`} />
          : <p className="text-xs text-muted-foreground">Guarda los datos técnicos para agregar la fotografía del patrón.</p>)}
      </div>
    ))}
    {ed && <div className="flex flex-wrap gap-2">{kinds.map((k) => <Button key={k} size="sm" variant="outline" onClick={() => set([...rows, { kind: k }])} data-testid={`td-add-${k}`}><Plus className="mr-1 h-4 w-4" />{ITEM_KINDS[k][0]}</Button>)}</div>}
  </div>
);

const Porton = ({ p, set, ed }) => (
  <div className="space-y-2 border-t border-border pt-3" data-testid="td-porton">
    <Head>Portón automático</Head>
    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
      <Sel label="Uso (periodicidad)" value={p.uso} options={[["privada", "Privada (cada 6 meses)"], ["particular", "Particular (cada 12 meses)"]]} disabled={!ed} onChange={(v) => set({ ...p, uso: v })} testid="td-porton-uso" />
      {PORTON_FIELDS.map(([f, l]) => <Txt key={f} label={l} value={p[f]} disabled={!ed} onChange={(v) => set({ ...p, [f]: v })} testid={`td-porton-${f}`} />)}
    </div>
    <div className="flex flex-wrap gap-4">{PORTON_TESTS.map(([t, l]) => <Check key={t} label={l} checked={p.pruebas?.[t]} disabled={!ed} onChange={(v) => set({ ...p, pruebas: { ...(p.pruebas || {}), [t]: v } })} testid={`td-porton-test-${t}`} />)}</div>
    <div className="w-40"><Txt label="Ciclos probados (mín. 3)" type="number" value={p.ciclos} disabled={!ed} onChange={(v) => set({ ...p, ciclos: v })} testid="td-porton-ciclos" /></div>
    <p className="text-xs text-muted-foreground">Fotos: antes, motor, tarjeta/control, receptor/seguridad y trabajo terminado.</p>
  </div>
);

const Review = ({ g, set, ed }) => (
  <div className="space-y-2" data-testid="td-gps-review">
    <Head>Revisión GPS — fallas detectadas</Head>
    <div className="grid grid-cols-2 gap-2">{GPS_FAILS.map(([k, l]) => <Check key={k} label={l} checked={(g.fallas || []).includes(k)} disabled={!ed}
      onChange={(v) => set({ ...g, fallas: v ? [...(g.fallas || []), k] : (g.fallas || []).filter((x) => x !== k) })} testid={`td-gps-fail-${k}`} />)}</div>
    <Check label="Incluye reubicación dentro del mismo vehículo" checked={g.reubicacion} disabled={!ed} onChange={(v) => set({ ...g, reubicacion: v })} testid="td-gps-reubicacion" />
    <div className="grid gap-2 md:grid-cols-2">
      <Txt label="Diagnóstico" value={g.diagnostico} disabled={!ed} onChange={(v) => set({ ...g, diagnostico: v })} testid="td-gps-diagnostico" />
      <Txt label="Trabajo realizado" value={g.trabajo_realizado} disabled={!ed} onChange={(v) => set({ ...g, trabajo_realizado: v })} testid="td-gps-trabajo" />
      <Sel label="Resultado *" value={g.resultado} options={GPS_RESULTS} disabled={!ed} onChange={(v) => set({ ...g, resultado: v })} testid="td-gps-resultado" />
      <Txt label="Observaciones" value={g.observaciones} disabled={!ed} onChange={(v) => set({ ...g, observaciones: v })} testid="td-gps-observaciones" />
    </div>
  </div>
);

const Maint = ({ g, set, ed }) => (
  <div className="space-y-2" data-testid="td-gps-maintenance">
    <Head>Mantenimiento GPS</Head>
    <div className="grid gap-2 md:grid-cols-2">
      <Txt label="Trabajo realizado" value={g.trabajo_realizado} disabled={!ed} onChange={(v) => set({ ...g, trabajo_realizado: v })} testid="td-gps-mant-trabajo" />
      <Sel label="Resultado *" value={g.resultado} options={GPS_RESULTS} disabled={!ed} onChange={(v) => set({ ...g, resultado: v })} testid="td-gps-mant-resultado" />
      <Txt label="Observaciones" value={g.observaciones} disabled={!ed} onChange={(v) => set({ ...g, observaciones: v })} testid="td-gps-mant-observaciones" />
    </div>
  </div>
);

const Uninstall = ({ u, set, ed }) => (
  <div className="space-y-2" data-testid="td-gps-uninstall">
    <Head>Desinstalación GPS</Head>
    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
      <Txt label="Motivo" value={u.motivo} disabled={!ed} onChange={(v) => set({ ...u, motivo: v })} testid="td-un-motivo" />
      <Sel label="Condición del equipo *" value={u.condicion} options={UNINSTALL_COND} disabled={!ed} onChange={(v) => set({ ...u, condicion: v })} testid="td-un-condicion" />
      <Sel label="Destino *" value={u.destino} options={DESTINOS} disabled={!ed} onChange={(v) => set({ ...u, destino: v })} testid="td-un-destino" />
      <Txt label={u.destino === "tssmx" ? "Responsable *" : "Responsable"} value={u.responsable} disabled={!ed} onChange={(v) => set({ ...u, responsable: v })} testid="td-un-responsable" />
      <Txt label={u.destino === "tssmx" ? "Fecha de recepción *" : "Fecha de recepción"} type="date" value={u.fecha_custodia} disabled={!ed} onChange={(v) => set({ ...u, fecha_custodia: v })} testid="td-un-fecha" />
      <Txt label={u.condicion === "no_localizado" ? "Observaciones *" : "Observaciones"} value={u.observaciones} disabled={!ed} onChange={(v) => set({ ...u, observaciones: v })} testid="td-un-observaciones" />
    </div>
  </div>
);

export const TechDataEditor = ({ wo, editable, onSaved }) => {
  const [d, setD] = useState(wo.tech_data || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => setD(wo.tech_data || {}), [wo.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const kinds = KINDS_BY_BU[wo.business_unit_code] || [];
  const gps = wo.business_unit_code === "gps";
  const review = gps && wo.service_type_code === "revision";
  const uninstall = gps && wo.service_type_code === "desinstalacion" && !!wo.tech_data?.gps_uninstall;
  const porton = wo.business_unit_code === "control_acceso";
  const maint = gps && wo.service_type_code === "mant_preventivo";
  if (!kinds.length && !review && !uninstall && !maint) return null;
  const set = (k) => (v) => setD((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    const body = { ...(kinds.length ? { items: d.items || [] } : {}), ...(porton && d.porton ? { porton: d.porton } : {}),
      ...(review ? { gps_review: d.gps_review || {} } : {}), ...(uninstall ? { gps_uninstall: d.gps_uninstall || {} } : {}), ...(maint ? { gps_maintenance: d.gps_maintenance || {} } : {}) };
    try { const { data } = await api.put(`/work-orders/${wo.id}/tech-data`, body); setD(data); onSaved?.(data); toast.success("Datos técnicos guardados"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="tech-data-editor">
      {kinds.length > 0 && <Items kinds={kinds} rows={d.items || []} set={set("items")} ed={editable} woId={wo.business_unit_code === "videovigilancia" ? wo.id : null} />}
      {porton && (d.porton ? <Porton p={d.porton} set={set("porton")} ed={editable} />
        : editable && <Button size="sm" variant="outline" onClick={() => set("porton")({ pruebas: {}, ciclos: 0 })} data-testid="td-add-porton"><Plus className="mr-1 h-4 w-4" />Agregar sección de portón</Button>)}
      {review && <Review g={d.gps_review || {}} set={set("gps_review")} ed={editable} />}
      {uninstall && <Uninstall u={d.gps_uninstall || {}} set={set("gps_uninstall")} ed={editable} />}
      {maint && <Maint g={d.gps_maintenance || {}} set={set("gps_maintenance")} ed={editable} />}
      {editable && <Button onClick={save} disabled={busy} data-testid="tech-data-save-button">Guardar datos técnicos</Button>}
    </div>
  );
};
